import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiCheck, FiPhone, FiClock, FiAlertTriangle } = FiIcons;

const PricingSection = ({ onBooking }) => {
  const alternatives = [
    {
      title: "ปรึกษาโหรทั่วไป",
      price: "1,500-3,000 บาท/ครั้ง",
      problems: [
        "ได้แค่คำทำนาย ไม่ได้วิธีแก้",
        "ไม่มีเครื่องมือให้ใช้",
        "ต้องไปปรึกษาซ้ำบ่อยๆ",
        "ไม่รู้หลักการ ต้องพึ่งพาตลอด"
      ]
    },
    {
      title: "ซื้อยันต์ตามร้าน",
      price: "5,000-15,000 บาท",
      problems: [
        "ยันต์ทั่วไป ไม่เฉพาะเจาะจง",
        "ไม่รู้วิธีใช้ที่ถูกต้อง",
        "ไม่มีการวิเคราะห์ดวงก่อน",
        "อาจไม่เหมาะกับดวงชะตา"
      ]
    },
    {
      title: "เรียนปาจื้อที่อื่น",
      price: "15,000-50,000 บาท",
      problems: [
        "เรียนนาน หลายเดือน",
        "ทฤษฎีมาก ปฏิบัติน้อย",
        "ไม่เน้นด้านการเงินโดยเฉพาะ",
        "ไม่ได้เครื่องมือใช้งานจริง"
      ]
    }
  ];

  return (
    <section className="py-16 px-4 relative overflow-hidden">
      {/* Enhanced Background */}
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751395126519-blob" 
          alt="Background" 
          className="absolute top-0 right-0 w-1/2 h-full object-cover rounded-l-3xl" 
        />
        <img 
          src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751394399737-blob" 
          alt="Background" 
          className="absolute bottom-0 left-0 w-1/2 h-2/3 object-cover rounded-tr-3xl opacity-80" 
        />
      </div>
      
      <div className="container mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            เปรียบเทียบทางเลือกอื่น
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            ทำไมหลักสูตร<span className="text-amber-400 font-semibold">วิชาปาจื้อเรียกทรัพย์</span>ของเราถึงคุ้มค่าที่สุด
          </p>
        </motion.div>

        {/* Comparison Table */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {alternatives.map((alt, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 border border-red-500/20"
            >
              <div className="text-center mb-6">
                <h3 className="text-lg font-bold text-white mb-2">{alt.title}</h3>
                <p className="text-red-400 font-semibold">{alt.price}</p>
              </div>
              <div className="space-y-3">
                {alt.problems.map((problem, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <SafeIcon icon={FiX} className="text-red-400 text-lg flex-shrink-0 mt-0.5" />
                    <p className="text-slate-300 text-sm">{problem}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Our Solution */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-gradient-to-br from-green-500/20 to-emerald-600/20 backdrop-blur-sm rounded-2xl p-8 border-2 border-green-500/40 relative overflow-hidden">
            {/* Highlight badge */}
            <div className="absolute top-6 right-6 bg-gradient-to-r from-amber-500 to-yellow-600 text-white px-4 py-2 rounded-full text-sm font-bold animate-pulse">
              ตัวเลือกที่ดีที่สุด!
            </div>
            
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-green-400 mb-4">
                หลักสูตร วิชาปาจื้อเรียกทรัพย์
              </h3>
              <div className="flex items-center justify-center gap-4 mb-4">
                <span className="text-2xl text-slate-400 line-through">6,500 บาท</span>
                <span className="text-4xl font-bold text-green-400">5,500 บาท</span>
              </div>
              <p className="text-amber-400 font-semibold">
                เพียง 1 วัน • เรียนครั้งเดียวใช้ได้ตลอดชีวิต<br />
                <span className="text-sm text-slate-300">(แต่ยันต์ประจำตัวมีอายุ 1 ปี)</span>
              </p>
            </div>
            
            {/* Critical Deadline Warning */}
            <div className="bg-gradient-to-r from-red-500/20 to-orange-600/20 rounded-xl p-6 border border-red-500/30 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <SafeIcon icon={FiAlertTriangle} className="text-red-400 text-2xl" />
                <h4 className="text-xl font-bold text-red-400">ข้อจำกัดสำคัญ</h4>
              </div>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-red-400 font-semibold mb-2">⏰ ปิดรับสมัคร</p>
                  <p className="text-white">9 กรกฎาคม 2568 (23.59 น.)</p>
                  <p className="text-slate-300 text-xs mt-1">เพื่อเตรียมยันต์และวิเคราะห์ดวง</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-purple-400 font-semibold mb-2">🔮 ยันต์ "ฮู้" จีน</p>
                  <p className="text-white">20 คนแรกเท่านั้น</p>
                  <p className="text-slate-300 text-xs mt-1">มากกว่านี้ทำไม่ทัน</p>
                </div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h4 className="text-xl font-bold text-white mb-4">✅ สิ่งที่คุณจะได้</h4>
                <div className="space-y-3">
                  {[
                    "ความรู้ปาจื้อเฉพาะด้านการเงิน",
                    "วิเคราะห์ดวงชะตาส่วนตัว",
                    "ยันต์ \"ฮู้\" จีนประจำตัว (20 คนแรก)",
                    "ปฏิทินฤกษ์เฉพาะบุคคล",
                    "วิธีเรียกทรัพย์ที่เหมาะกับดวง",
                    "ใช้ความรู้ได้ตลอดชีวิต"
                  ].map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <SafeIcon icon={FiCheck} className="text-green-400 text-lg flex-shrink-0 mt-0.5" />
                      <p className="text-white">{benefit}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-xl font-bold text-white mb-4">💰 คุณค่าที่แท้จริง</h4>
                <div className="bg-slate-800/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-300">ค่าเรียน</span>
                    <span className="text-white">5,500 บาท</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-300">แผ่นดวง</span>
                    <span className="text-green-400">3,000 บาท</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-300">ยันต์ "ฮู้" จีน</span>
                    <span className="text-green-400">12,000 บาท</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-300">ปฏิทินฤกษ์</span>
                    <span className="text-green-400">2,000 บาท</span>
                  </div>
                  <div className="border-t border-slate-600 pt-2 mt-2">
                    <div className="flex justify-between font-bold">
                      <span className="text-white">รวมคุณค่า</span>
                      <span className="text-green-400">22,500 บาท</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <div className="bg-gradient-to-r from-red-500/20 to-orange-600/20 rounded-lg p-4 mb-6 border border-red-500/30">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <SafeIcon icon={FiClock} className="text-red-400 text-lg" />
                  <span className="text-red-400 font-semibold">ต้องจองก่อน 9 ก.ค. เท่านั้น!</span>
                </div>
                <p className="text-white mb-2">
                  ราคาพิเศษ 5,500 บาท สำหรับ 10 คนแรก
                </p>
                <p className="text-amber-300 text-sm">
                  หลักสูตรรองรับสูงสุด 35 คน | ยันต์ "ฮู้" จีน 20 คนแรก
                </p>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onBooking}
                className="bg-gradient-to-r from-red-500 to-orange-600 text-white px-12 py-4 rounded-full text-xl font-bold shadow-2xl hover:shadow-red-500/25 transition-all duration-300 mb-4 flex items-center gap-3 mx-auto animate-pulse"
              >
                <SafeIcon icon={FiPhone} className="text-2xl" />
                จองออนไลน์เลย!
              </motion.button>
              
              <p className="text-slate-400">
                💳 รับชำระเงินสด | โอนเงิน | บัตรเครดิต
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;